"use client";
import { useState, useEffect, useRef } from "react";

const BUCKET_COLORS = [
  { bg: "#dcfce7", border: "#86efac", text: "#14532d", bar: "#22c55e" },
  { bg: "#fef9c3", border: "#fde047", text: "#854d0e", bar: "#eab308" },
  { bg: "#fff7ed", border: "#fdba74", text: "#9a3412", bar: "#f97316" },
  { bg: "#fff7ed", border: "#fdba74", text: "#9a3412", bar: "#f97316" },
  { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b", bar: "#ef4444" },
  { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b", bar: "#ef4444" },
  { bg: "#fdf4ff", border: "#d8b4fe", text: "#6b21a8", bar: "#a855f7" },
  { bg: "#eff6ff", border: "#93c5fd", text: "#1e3a8a", bar: "#3b82f6" },
];

export default function BucketList({ schoolID, itemMinPrice, itemMaxPrice, categories }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});  // tracks which buckets are expanded
  const abortRef = useRef(null);

  useEffect(() => {
    if (!schoolID) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setExpanded({});  // reset expanded state when school/filters change

    const params = new URLSearchParams();
    if (itemMinPrice) params.set("itemMinPrice", itemMinPrice);
    if (itemMaxPrice) params.set("itemMaxPrice", itemMaxPrice);
    if (categories?.length) params.set("categories", categories.join(","));

    fetch(`/api/wishlist/${schoolID}/items?${params.toString()}`, { signal: controller.signal })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { if (e.name !== "AbortError") setLoading(false); });

    return () => controller.abort();
  }, [schoolID, itemMinPrice, itemMaxPrice, categories]);

  const bucketMap = {};
  (data?.buckets || []).forEach(b => { bucketMap[b.label] = b; });

  const ALL_BUCKETS = [
    "$5 – $10", "$10 – $20", "$20 – $30", "$30 – $40",
    "$40 – $50", "$50 – $60", "$60 – $75", "$75+",
  ];

  const toggleExpanded = (label) => {
    setExpanded(prev => ({ ...prev, [label]: !prev[label] }));
  };

  if (!data && loading) return (
    <div style={{ textAlign: "center", color: "#f97316", fontSize: 13, padding: 16 }}>
      🎁 Loading items...
    </div>
  );

  if (!data?.buckets?.length && !loading) return (
    <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: 16 }}>
      All items purchased! 🎉
    </div>
  );
  return (
    <div style={{ padding: "0 12px 16px", position: "relative"}}>

      {/* Subtle overlay while updating */}
      {loading && data && (
        <div style={{
          position: "absolute", inset: 0,
          background: "rgba(255,251,245,0.6)",
          display: "flex", alignItems: "flex-start", justifyContent: "center",
          paddingTop: 16, zIndex: 5, pointerEvents: "none",
        }}>
          <div style={{ fontSize: 11, color: "#f97316", fontWeight: 700, background: "#fffbf5", padding: "3px 10px", borderRadius: 999, border: "2px solid #fde68a" }}>
            Updating...
          </div>
        </div>
      )}

      {data?.teacher?.wishlistUrl && (
        <a href={data.teacher.wishlistUrl} target="_blank" rel="noopener noreferrer" style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          padding: "9px 0", marginBottom: 12, background: "#ffffff",
          color: "#7c2d12", borderRadius: 999, fontSize: 12, fontWeight: 900,
          textDecoration: "none", boxShadow: "2px 2px 2px 2px #ca8a04",
        }}>
          🎁 Shop This List →
        </a>
      )}

      {(data?.buckets || []).map((bucket, i) => {
        const c          = BUCKET_COLORS[i % BUCKET_COLORS.length];
        const isExpanded = expanded[bucket.label] || false;
        const allTitles  = bucket.product_titles || [];
        const preview    = allTitles.slice(0, 6);
        const hidden     = allTitles.slice(6);
        const hasMore    = bucket.count > preview.length;

        // Which titles to show
        const visibleTitles = isExpanded ? allTitles : preview;

        return (
          <div key={bucket.label} style={{
            background: c.bg, border: `2px solid ${c.border}`,
            borderRadius: 12, padding: "10px 12px", marginBottom: 8,
            boxShadow: `2px 2px 0 ${c.border}`
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: c.text }}>{bucket.label}</div>
            </div>

            {visibleTitles.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {visibleTitles.map((name, ni) => (
                  <span key={ni} style={{
                    fontSize: 10, fontWeight: 700,
                    background: "rgba(255,255,255,0.85)", border: `1px solid ${c.border}`,
                    borderRadius: 999, padding: "2px 8px", color: c.text,
                  }}>{name}</span>
                ))}

                {/* Show more / show less button */}
                {hasMore && (
                  <button
                    onClick={() => toggleExpanded(bucket.label)}
                    style={{
                      fontSize: 10, fontWeight: 700,
                      background: isExpanded ? "rgba(255,255,255,0.85)" : c.bar,
                      border: isExpanded ? `1px solid ${c.border}` : "none",
                      borderRadius: 999, padding: "2px 8px",
                      color: isExpanded ? c.text : "#fff",
                      cursor: "pointer",
                      transition: "all .15s",
                    }}
                  >
                    {isExpanded
                      ? "Show less ↑"
                      : `+${bucket.count - preview.length} more ↓`}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}